import { APITypesV1 as APITypes } from "@cur8/api-client";
import { AnnotationURI, PatientBlobURI } from "@cur8/uri";
import { NotificationType } from "render/components/Notification";
import { APIClient } from "render/context/APIContext";
import { v4 as uuid } from "uuid";
import { isErrorOfCode } from "./isErrorOfCode";

/**
 * Upload file to patient blob store and create annotation
 */
export async function uploadFile(
  file: File,
  patientId: string,
  linkedAnnotationId: string,
  physicalArtefactId: string,
  api: APIClient,
  notificationCb: (payload: NotificationType) => void
) {
  if (!patientId) {
    notificationCb({
      type: "error",
      message: "Has no patientId when trying to upload file",
    });
    return null;
  }

  const filename = file.name.toLowerCase();
  // Check that it is an image file
  if (
    !(
      filename.endsWith(".jpeg") ||
      filename.endsWith(".jpg") ||
      filename.endsWith(".png") ||
      filename.endsWith(".jfif")
    )
  ) {
    notificationCb({
      type: "error",
      message: "Only .jpg, .jpeg, .jfif and .png currently supported",
    });
    return null;
  }
  const ending = filename.split(".").pop();

  const path = `files/${uuid().replace(/-/g, "")}.${ending}`;

  try {
    await api.blob.putPatientBlob({
      patientId,
      file,
      path,
    }).result;
  } catch (error) {
    console.error("Could not post image: ", error);
    notificationCb({
      type: "error",
      message: "Could not upload image",
    });
    return null;
  }

  const targetUri = new PatientBlobURI(patientId, path);
  const hasPid = physicalArtefactId.length > 0;

  const newAnno = await createAnnotation(
    patientId,
    physicalArtefactId,
    api,
    notificationCb,
    targetUri
  );

  // failed to create annotation
  if (!newAnno) {
    // delete the uploaded file, a new attempt will upload it again
    api.blob
      .deletePatientBlob({
        patientId,
        filePathAndName: path,
      })
      .result.catch(console.error);
    return null;
  }

  // Create linked annotation for DoctorUI to find Derma images
  if (linkedAnnotationId && hasPid) {
    createLinkedAnnotation(
      patientId,
      physicalArtefactId,
      linkedAnnotationId,
      api,
      notificationCb,
      targetUri
    );
  }

  return newAnno;
}

function createAnnotation(
  patientId: string,
  physicalArtefactId: string,
  api: APIClient,
  notificationCb: (payload: NotificationType) => void,
  targetUri: PatientBlobURI
) {
  const hasPid = physicalArtefactId.length > 0;

  return api.annotation
    .createAnnotation(
      { patientId },
      {
        targetUri: targetUri.toString(),
        physicalArtefactId: hasPid ? physicalArtefactId : undefined,
        applicationSpecificTarget: hasPid ? "derma:derma" : "derma:unlinked",
        acceptState: APITypes.AcceptState.Accepted,
      }
    )
    .result.then((annotation) => {
      console.log("created annotation:", annotation);
      notificationCb({
        type: "success",
        message: "Image uploaded",
      });
      return annotation;
    })
    .catch((error) => {
      console.error("Could not create annotation:", error);
      const message = isErrorOfCode(409, error)
        ? "No derma assessment in progress"
        : "Could not create annotation";
      notificationCb({ type: "error", message });
      return null;
    });
}

function createLinkedAnnotation(
  patientId: string,
  physicalArtefactId: string,
  linkedAnnotationId: string,
  api: APIClient,
  notificationCb: (payload: NotificationType) => void,
  targetUri: PatientBlobURI
) {
  const annotationUri = new AnnotationURI(patientId, linkedAnnotationId);
  api.annotation
    .createAnnotation(
      { patientId },
      {
        targetUri: annotationUri.toString(),
        physicalArtefactId: physicalArtefactId,
        applicationSpecificTarget: targetUri.toString(),
        acceptState: APITypes.AcceptState.Accepted,
      }
    )
    .result.then((annotation) => {
      console.log("created linked annotation:", annotation);
      notificationCb({
        type: "success",
        message: "Image uploaded",
      });
      return annotation;
    })
    .catch((e) => {
      console.error("Could not create annotation:", e);
      notificationCb({
        type: "error",
        message: "Could not create annotation",
      });
      return null;
    });
}
